import { useEffect, useState } from 'react';
import { Instagram, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { INSTAGRAM_URL } from '../content';
import { FadeUp } from './Reveal';

const FEED_ID = process.env.REACT_APP_BEHOLD_FEED_ID;

const imageFor = (post) => {
    if (post?.sizes?.medium?.mediaUrl) return post.sizes.medium.mediaUrl;
    if (post?.mediaType === 'VIDEO' && post.thumbnailUrl) return post.thumbnailUrl;
    return post?.mediaUrl;
};

const InstagramGrid = () => {
    const { t } = useLanguage();
    const [posts, setPosts] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!FEED_ID) return;
        let cancelled = false;
        fetch(`https://feeds.behold.so/${FEED_ID}`, { headers: { Accept: 'application/json' } })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                if (cancelled) return;
                const list = Array.isArray(data?.posts) ? data.posts.slice(0, 6) : [];
                setPosts(list);
            })
            .catch(() => !cancelled && setError(true));
        return () => {
            cancelled = true;
        };
    }, []);

    // If the feed ID is not configured yet, hide the whole section quietly.
    if (!FEED_ID) return null;

    return (
        <section
            className="border-t border-white/10 bg-ink"
            data-testid="instagram-section"
            aria-label="Instagram"
        >
            <div className="px-6 md:px-12 py-32 md:py-40 max-w-[1400px] mx-auto">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
                    <FadeUp>
                        <span className="text-xs tracking-[0.35em] uppercase text-dusk flex items-center gap-3">
                            <Instagram className="w-4 h-4" strokeWidth={1.5} />
                            {t.instagram.kicker}
                        </span>
                        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-paper" data-testid="instagram-title">
                            {t.instagram.title}
                        </h2>
                        <p className="mt-4 text-sm md:text-base text-fog max-w-md">{t.instagram.sub}</p>
                    </FadeUp>
                    <FadeUp delay={0.15} className="shrink-0">
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center gap-3 rounded-full border border-white/25 px-6 py-3 text-sm text-paper transition-colors duration-300 hover:border-dusk hover:text-dusk"
                            data-testid="instagram-follow-link"
                        >
                            @robinspencer.artist
                            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </FadeUp>
                </div>

                {error && (
                    <p className="text-sm text-fog" data-testid="instagram-error">
                        {t.instagram.error}
                    </p>
                )}

                {!error && (
                    <div
                        className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5"
                        data-testid="instagram-grid"
                    >
                        {(posts ?? Array.from({ length: 6 })).map((post, i) => {
                            if (!post) {
                                return (
                                    <div
                                        key={`skeleton-${i}`}
                                        className="aspect-square bg-surface animate-pulse"
                                        data-testid={`instagram-skeleton-${i}`}
                                    />
                                );
                            }
                            const src = imageFor(post);
                            return (
                                <motion.a
                                    key={post.id}
                                    href={post.permalink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                                    className="group relative block aspect-square overflow-hidden spotlight"
                                    data-testid={`instagram-post-${i}`}
                                    aria-label={post.altText || post.caption?.slice(0, 80) || 'Instagram post'}
                                >
                                    <img
                                        src={src}
                                        alt={post.altText || post.caption?.slice(0, 120) || 'Instagram post'}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                                    />
                                    {post.mediaType === 'VIDEO' && (
                                        <span className="absolute top-3 right-3 rounded-full bg-ink/70 backdrop-blur px-3 py-1 text-[10px] tracking-[0.25em] uppercase text-paper">
                                            Video
                                        </span>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <ArrowUpRight className="absolute bottom-4 right-4 w-5 h-5 text-paper opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                </motion.a>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

export default InstagramGrid;
